import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { massMigrationApi } from '@/lib/api/massMigration';
import {
  useMassMigrationWizardParsedCsv,
  useMassMigrationWizardExcludedRows,
  useMassMigrationWizardSelections,
  useMassMigrationWizardOverrides,
  useMassMigrationWizardUserMigrationRows,
  useMassMigrationWizardOrgType,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import { WizardNavigation } from './WizardNavigation';
import { StatTile } from './StatTile';

// ─── ConfirmStep ──────────────────────────────────────────────────────────────

export function ConfirmStep() {
  const parsedCsv = useMassMigrationWizardParsedCsv();
  const excludedRows = useMassMigrationWizardExcludedRows();
  const selectionsByRow = useMassMigrationWizardSelections();
  const overridesByRow = useMassMigrationWizardOverrides();
  const userMigrationEnabledRows = useMassMigrationWizardUserMigrationRows();
  const orgType = useMassMigrationWizardOrgType();
  const { reset, setStep } = useMassMigrationWizardActions();
  const navigate = useNavigate();

  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  const allRows = parsedCsv?.rows ?? [];
  const activeRows = allRows.filter((r) => !excludedRows.includes(r.rowNumber));
  const activeOrgCount = activeRows.length;

  const expectedConfirmText = `MIGRATE ${activeOrgCount}`;
  const canSubmit =
    acknowledged &&
    confirmInput === expectedConfirmText;

  // Compute stats
  const totalModules = activeRows.reduce(
    (sum, r) => sum + (selectionsByRow[r.rowNumber] ?? []).length,
    0,
  );
  const estMinutes = Math.ceil(activeOrgCount / 10) * 2;

  const submitMutation = useMutation({
    mutationFn: () => {
      const resolvedOrgType = orgType ?? 'New';
      return massMigrationApi.startBatch({
        name: `Mass Migration ${new Date().toISOString().split('T')[0]}`,
        orgType: resolvedOrgType,
        rows: activeRows,
        selectionsByRow,
        overridesByRow,
        userMigrationEnabledRows,
      });
    },
    onSuccess: (data) => {
      reset();
      void navigate({ to: '/migrations/batches/$batchId', params: { batchId: data.batchId } });
    },
  });

  const handleSubmit = () => {
    if (canSubmit) {
      submitMutation.mutate();
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Organizations" value={activeOrgCount} />
        <StatTile label="Modules to migrate" value={totalModules} />
        <StatTile label="Est. duration" value={`~${estMinutes} min`} />
      </div>

      {/* Exclusions alert */}
      {excludedRows.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>
            {excludedRows.length} organization{excludedRows.length !== 1 ? 's' : ''} excluded
          </AlertTitle>
          <AlertDescription>
            {excludedRows.length} organization{excludedRows.length !== 1 ? 's have' : ' has'} been
            excluded from this batch and will not be migrated.
          </AlertDescription>
        </Alert>
      )}

      {/* Planned actions list */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-foreground">Planned actions</h3>
        <div
          className="max-h-[400px] overflow-y-auto rounded-md border border-border divide-y divide-border"
          role="list"
          aria-label="Organizations to migrate"
        >
          {activeRows.map((row) => {
            const orgName = row.orgName ?? row.bridgeId;
            const modules = selectionsByRow[row.rowNumber] ?? [];

            return (
              <div
                key={row.rowNumber}
                role="listitem"
                className="flex items-start gap-3 px-4 py-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{orgName}</span>
                    <Badge variant={orgType === 'New' ? 'default' : 'secondary'} className="text-xs">
                      {orgType ?? 'New'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{row.bridgeId}</p>
                  {modules.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {modules.map((key) => (
                        <span
                          key={key}
                          className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {key}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {activeRows.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No active organizations to migrate.
            </div>
          )}
        </div>
      </div>

      {/* Warning notice */}
      <Alert>
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>This action cannot be paused or cancelled</AlertTitle>
        <AlertDescription>
          Once started, this mass migration cannot be paused or cancelled mid-execution.
          Migrations already in progress will run to completion.
        </AlertDescription>
      </Alert>

      {/* Acknowledgement + confirmation */}
      <div className="rounded-md border border-border p-4 space-y-4">
        <div className="flex items-start gap-3">
          <Checkbox
            id="acknowledge"
            checked={acknowledged}
            onCheckedChange={(checked) => setAcknowledged(checked === true)}
            aria-label="Acknowledge mass migration"
          />
          <Label htmlFor="acknowledge" className="text-sm leading-relaxed cursor-pointer">
            I confirm that I have reviewed all{' '}
            <span className="font-semibold">{activeOrgCount}</span>{' '}
            organization{activeOrgCount !== 1 ? 's' : ''} and understand that this mass
            migration cannot be undone.
          </Label>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm-input" className="text-sm font-medium text-foreground">
            Type{' '}
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {expectedConfirmText}
            </span>{' '}
            to confirm
          </Label>
          <Input
            id="confirm-input"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={expectedConfirmText}
            className={cn(
              'max-w-xs font-mono',
              confirmInput !== '' && confirmInput !== expectedConfirmText
                ? 'border-destructive focus-visible:ring-destructive'
                : '',
            )}
            aria-describedby="confirm-input-hint"
          />
          <p id="confirm-input-hint" className="text-xs text-muted-foreground">
            Case-sensitive exact match required.
          </p>
        </div>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || submitMutation.isPending}
          className="w-full sm:w-auto"
        >
          {submitMutation.isPending ? (
            <>
              <span
                className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
              Starting batch…
            </>
          ) : (
            `Start Mass Migration (${activeOrgCount} org${activeOrgCount !== 1 ? 's' : ''})`
          )}
        </Button>
      </div>

      {/* Submission error */}
      {submitMutation.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Failed to start mass migration</AlertTitle>
          <AlertDescription>
            <p>
              {submitMutation.error instanceof Error
                ? submitMutation.error.message
                : 'An unexpected error occurred. Please try again.'}
            </p>
            <p>Please go back and review the batch configuration, then try again.</p>
          </AlertDescription>
        </Alert>
      )}

      <WizardNavigation
        onBack={() => setStep(5)}
        onNext={undefined}
      />
    </div>
  );
}
